import { defineStore } from 'pinia';
import { ref, computed, type ComputedRef } from 'vue';
import type { RemoteConfig } from 'firebase/remote-config';
import { getValue } from 'firebase/remote-config';
import {
  fetchAndActivateConfig,
  setupRealtimeListener,
} from '../services/remoteConfig.service';
import type {
  RemoteConfigKey,
  RemoteConfigValueType,
} from '../types/remoteConfig.types';
import { REMOTE_CONFIG_TYPED_DEFAULTS } from '../constants/remoteConfig.constant';
import { INITIAL_STATE } from '../constants/store.constant';
import { resolveFeatureFlag } from '../utils/featureFlagSegmentation.util';
import { readSegmentationContext } from '../utils/segmentationContext.util';

export const useRemoteConfigStore = defineStore('remoteConfig', () => {
  const remoteConfig = ref<RemoteConfig | null>(INITIAL_STATE.remoteConfig);
  const isInitialized = ref(INITIAL_STATE.isInitialized);
  const isRealtimeActive = ref(INITIAL_STATE.isRealtimeActive);
  const lastUpdate = ref<Date | null>(INITIAL_STATE.lastUpdate);
  const updateTrigger = ref(INITIAL_STATE.updateTrigger);

  let unsubscribeFn: (() => void) | null = null;
  let initializationPromise: Promise<void> | null = null;

  /**
   * Incrementa el trigger para forzar una reevaluación
   */
  function triggerUpdate() {
    updateTrigger.value++;
  }

  /**
   * @description Helper para extraer valores de Remote Config con type safety
   * @param value - Valor de Remote Config
   * @param defaultValue - Valor por defecto
   * @returns El tipo correcto basado en el tipo inferido
   */
  function extractValue<T extends boolean | number | string | object>(
    value: ReturnType<typeof getValue>,
    defaultValue: T
  ): T {
    const type = typeof defaultValue;

    switch (type) {
      case 'boolean':
        return value.asBoolean() as T;
      case 'number':
        return value.asNumber() as T;
      case 'object':
        try {
          const stringValue = value.asString();
          // Si el string está vacío o es "[object Object]", usar el valor por defecto
          if (!stringValue || stringValue === '[object Object]' || stringValue.trim() === '') {
            console.warn('[Remote Config] Valor inválido para objeto, usando valor por defecto:', stringValue);
            return defaultValue;
          }
          return JSON.parse(stringValue) as T;
        } catch (error) {
          console.error('[Remote Config] Error al parsear JSON, usando valor por defecto:', error);
          return defaultValue;
        }
      default:
        return value.asString() as T;
    }
  }

  /**
   * Obtiene un valor de Remote Config de forma reactiva
   *
   * @param key - Key del Remote Config
   * @param fallback - Valor de fallback (opcional, usa REMOTE_CONFIG_DEFAULTS si no se proporciona)
   */
  function getRemoteConfigValue<K extends RemoteConfigKey>(
    key: K,
    fallback?: RemoteConfigValueType<K>
  ): ComputedRef<RemoteConfigValueType<K>> {
    return computed(() => {
      // Dependencia reactiva - fuerza re-evaluación cuando cambia
      void updateTrigger.value;

      const defaultValue = (fallback !== undefined 
        ? fallback 
        : REMOTE_CONFIG_TYPED_DEFAULTS[key]) as RemoteConfigValueType<K>;

      if (!remoteConfig.value) {
        return defaultValue;
      }

      const value = getValue(remoteConfig.value, key);
      const extractedValue = extractValue(value, defaultValue);

      return extractedValue;
    });
  }

  /**
   * Obtiene un feature flag del nuevo contrato (objeto con `enabled` obligatorio
   * y `segmentation` opcional) ya resuelto contra el contexto del usuario.
   *
   * Conserva la firma de `getRemoteConfigValue` (devuelve `ComputedRef`) pero
   * pipea el valor por `resolveFeatureFlag`, mutando solo `enabled` y dejando el
   * resto del shape intacto.
   *
   * Convive con `getRemoteConfigValue` para permitir migración gradual: las
   * flags legacy (boolean plano, `{ isActive }`, etc.) se siguen leyendo con el
   * método anterior; las flags del nuevo contrato usan este.
   */
  function getFeatureFlag<K extends RemoteConfigKey>(
    key: K,
    fallback?: RemoteConfigValueType<K>
  ): ComputedRef<RemoteConfigValueType<K>> {
    return computed(() => {
      // Dependencia reactiva — fuerza re-evaluación cuando cambia Remote Config
      void updateTrigger.value;

      const defaultValue = (fallback !== undefined
        ? fallback
        : REMOTE_CONFIG_TYPED_DEFAULTS[key]) as RemoteConfigValueType<K>;

      if (!remoteConfig.value) {
        return defaultValue;
      }

      const value = getValue(remoteConfig.value, key);
      const extractedValue = extractValue(value, defaultValue);

      const segmentationContext = readSegmentationContext();
      const resolvedValue = resolveFeatureFlag(extractedValue, segmentationContext);

      return resolvedValue;
    });
  }

  /**
   * @description Inicializa Remote Config y activa las actualizaciones en tiempo real. Debe llamarse una sola vez.
   * @returns Promise<void>
   */
  async function initialize(): Promise<void> {
    if (isInitialized.value) return;
    if (initializationPromise) return initializationPromise;

    initializationPromise = (async () => {
      try {
        if (!remoteConfig.value) {
          throw new Error('Remote Config no disponible');
        }

        await fetchAndActivateConfig(remoteConfig.value);

        isInitialized.value = true;
        triggerUpdate();

        startRealtimeUpdates();
      } catch (error) {
        console.error('[Remote Config] Error:', error);
      }
    })();

    return initializationPromise;
  }

  /**
   * Activa las actualizaciones en tiempo real.
   */
  function startRealtimeUpdates(): void {
    if (isRealtimeActive.value || !remoteConfig.value) return;

    unsubscribeFn = setupRealtimeListener(remoteConfig.value, () => {
      lastUpdate.value = new Date();
      triggerUpdate();
    });

    isRealtimeActive.value = true;
  }

  function stopRealtimeUpdates(): void {
    if (unsubscribeFn) {
      unsubscribeFn();
      unsubscribeFn = null;
      isRealtimeActive.value = false;
    }
  }

  function $reset(): void {}

  return {
    // State
    remoteConfig,
    isInitialized,
    isRealtimeActive,
    lastUpdate,

    // Actions
    initialize,
    stopRealtimeUpdates,
    triggerUpdate,
    $reset,

    getRemoteConfigValue,
    getFeatureFlag,
  };
});
